import React from 'react';
import { useTranslation } from 'react-i18next';
import { AttentionMessage, Box, VStack } from '@tq/ui-components';
import { useGetTeams } from 'api';
import { useTeamColumns } from '../../hooks';
import {
  teamPageNumberState,
  teamPageSizeState,
  teamSearchState,
  teamEditingRowIdState,
  teamSortByColumnFilterState,
  teamSortByDescendingFilterState,
  teamsGetRequestState,
} from 'store';
import { Organisation } from 'types';
import { SearchText, TeamTable } from 'components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { AddTeam } from './';

export type TeamsPanelProps = {
  organisation: Organisation | undefined;
};

export const TeamsPanel: React.FC<TeamsPanelProps> = ({ organisation }) => {
  const { t } = useTranslation('manage_organisation');
  const infoMsg = t(
    'team_info_message',
    'You must create an organisation before adding teams.'
  );
  const isMobile = useIsMobile();

  const columns = useTeamColumns(!!organisation?.email);

  const { data: teamsData } = useGetTeams(
    teamsGetRequestState,
    teamPageNumberState,
    organisation?.id,
    {
      enabled: !!organisation,
      refetchOnMount: false,
    }
  );

  return (
    <VStack pb={4} pt={1} alignItems='stretch'>
      {!organisation && (
        <AttentionMessage width='100%' status={'warning'} title={infoMsg} variant='inline' />
      )}
      <AddTeam organisation={organisation} />
      <Box marginBottom={5} width={isMobile ? '100%' : '30%'}>
        <SearchText
          isDisabled={!organisation}
          searchState={teamSearchState}
          placeholderText={t('search_placeholder', 'Search by name or email')}
          recordCount={teamsData?.teams.items.length || 0}
          totalCount={teamsData?.paginationInfo.totalCount || 0}
        />
      </Box>
      <TeamTable
        teams={teamsData?.teams.items || []}
        pagination={teamsData?.paginationInfo || undefined}
        pageNumberState={teamPageNumberState}
        pageSizeState={teamPageSizeState}
        columns={columns}
        sortByColumnState={teamSortByColumnFilterState}
        sortByDescendingState={teamSortByDescendingFilterState}
        editingState={teamEditingRowIdState}
      />
    </VStack>
  );
};
